import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ScanBarcode, Mail, Lock, User, Loader2, LogIn, UserPlus, AtSign, KeyRound, ArrowLeft, AlertCircle, ShieldCheck, History } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useLang } from '../i18n';
import { useAuth, getLastAuthMethod } from '../auth';
import { UsernameAvailability } from './UsernameAvailability';
import { Turnstile } from './Turnstile';

const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
    <path fill="#FBBC05" d="M5.85 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.67-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.67 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
  </svg>
);

/**
 * Shared auth form: Google (Emergent OAuth) + email/password (login/register).
 * Used inside the login dialog. On success calls onDone().
 */
const AuthForms = ({ onDone }) => {
  const { lang } = useLang();
  const { login, register, forgotPassword, resetPassword, config } = useAuth();
  const en = lang === 'en';
  const t = (de, eng) => (en ? eng : de);

  const siteKey = (config && config.turnstile_enabled) ? (config.turnstile_site_key || '') : '';
  const lastMethod = getLastAuthMethod();

  // view: 'auth' (login/register tabs) | 'forgot' (reset password flow)
  const [view, setView] = useState('auth');
  const [tab, setTab] = useState('login');
  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');
  // Cloudflare Turnstile tokens + "stay signed in" preference.
  const [turnstileRequired, setTurnstileRequired] = useState(false); // login: only after suspicion
  const [loginToken, setLoginToken] = useState('');
  const [registerToken, setRegisterToken] = useState('');
  const [rememberLogin, setRememberLogin] = useState(true);
  const [rememberRegister, setRememberRegister] = useState(true);
  const [rememberGoogle, setRememberGoogle] = useState(true);

  // "Last used" hint pill shown next to the method last used on this device.
  const LastUsedBadge = () => (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary"
      data-testid="auth-last-used-badge"
    >
      <History className="h-3 w-3" />{t('Zuletzt genutzt', 'Last used')}
    </span>
  );

  // Forgot-password flow: step 'request' (enter identifier) -> 'reset' (code + new pw)
  const [forgotStep, setForgotStep] = useState('request');
  const [forgotId, setForgotId] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotError, setForgotError] = useState('');

  const startGoogle = () => {
    // Remember the "stay signed in" choice so the OAuth callback can apply it
    // when it exchanges the session_id for an app session.
    try { localStorage.setItem('bc_google_remember', rememberGoogle ? '1' : '0'); } catch (e) { /* ignore */ }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const errText = (err, fallback) => {
    const msg = err?.response?.data?.detail;
    if (typeof msg === 'string') return msg;
    if (Array.isArray(msg) && msg[0]?.msg) return msg[0].msg;
    return fallback;
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    if (busy) return;
    setLoginError('');
    // A human check is required on every login — ensure it was completed.
    if (siteKey && !loginToken) {
      setLoginError(t('Bitte bestätige zuerst die Sicherheitsprüfung.', 'Please complete the security check first.'));
      return;
    }
    setBusy(true);
    try {
      await login(identifier.trim(), password, { turnstileToken: loginToken, remember: rememberLogin });
      toast.success(t('Willkommen zurück!', 'Welcome back!'));
      setTurnstileRequired(false); setLoginToken('');
      if (onDone) onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Server asks for a human check before allowing more attempts.
      if (detail && typeof detail === 'object' && detail.captcha_required) {
        setTurnstileRequired(true);
        setLoginToken('');
        setLoginError(detail.message || t('Bitte bestätige die Sicherheitsprüfung.', 'Please complete the security check.'));
        return;
      }
      // Account exists but was created via Google (no password): guide the user
      // to the Google button instead of showing a confusing "wrong password".
      if (detail && typeof detail === 'object' && detail.google_account) {
        const m = detail.message || t('Dieses Konto wurde mit Google erstellt. Bitte melde dich mit Google an.',
                                      'This account was created with Google. Please sign in with Google.');
        setLoginError(m);
        toast.info ? toast.info(m) : toast(m);
        return;
      }
      const msg = errText(err, t('Anmeldung fehlgeschlagen.', 'Login failed.'));
      // Reset the (single-use) Turnstile token after a wrong attempt so the
      // widget issues a fresh one.
      if (turnstileRequired) setLoginToken('');
      setLoginError(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (busy) return;
    setRegisterError('');
    if ((password || '').length < 8) {
      const m = t('Das Passwort muss mindestens 8 Zeichen haben.', 'Password must be at least 8 characters.');
      setRegisterError(m); toast.error(m); return;
    }
    if (siteKey && !registerToken) {
      setRegisterError(t('Bitte bestätige zuerst die Sicherheitsprüfung.', 'Please complete the security check first.'));
      return;
    }
    setBusy(true);
    try {
      await register(email.trim(), password, name.trim(), username.trim(), { turnstileToken: registerToken, remember: rememberRegister });
      toast.success(t('Konto erstellt – willkommen!', 'Account created – welcome!'));
      if (onDone) onDone();
    } catch (err) {
      const msg = errText(err, t('Registrierung fehlgeschlagen.', 'Registration failed.'));
      setRegisterToken(''); // refresh the single-use token for a retry
      setRegisterError(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const openForgot = () => {
    setForgotError('');
    setForgotStep('request');
    setForgotId(identifier.trim());
    setResetCode('');
    setNewPassword('');
    setView('forgot');
  };

  const submitForgotRequest = async (e) => {
    e.preventDefault();
    if (busy) return;
    setForgotError('');
    setBusy(true);
    try {
      await forgotPassword(forgotId.trim());
      toast.success(t('Falls ein Konto existiert, wurde ein Code gesendet.', 'If an account exists, a code has been sent.'));
      setForgotStep('reset');
    } catch (err) {
      setForgotError(errText(err, t('Anfrage fehlgeschlagen.', 'Request failed.')));
    } finally { setBusy(false); }
  };

  const submitForgotReset = async (e) => {
    e.preventDefault();
    if (busy) return;
    setForgotError('');
    if ((newPassword || '').length < 8) {
      setForgotError(t('Das Passwort muss mindestens 8 Zeichen haben.', 'Password must be at least 8 characters.'));
      return;
    }
    setBusy(true);
    try {
      await resetPassword(forgotId.trim(), resetCode.trim(), newPassword);
      toast.success(t('Passwort geändert. Bitte melde dich an.', 'Password changed. Please sign in.'));
      // Prefill login with the identifier and return to login tab.
      setIdentifier(forgotId.trim());
      setPassword('');
      setView('auth');
      setTab('login');
    } catch (err) {
      setForgotError(errText(err, t('Zurücksetzen fehlgeschlagen.', 'Reset failed.')));
    } finally { setBusy(false); }
  };

  const ErrorNote = ({ children }) => (
    <div
      className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
      role="alert"
      data-testid="auth-error"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  );

  // ----- Forgot-password view -----
  if (view === 'forgot') {
    return (
      <div className="space-y-5" data-testid="auth-forgot-view">
        <button
          type="button"
          onClick={() => { setView('auth'); setForgotError(''); }}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          data-testid="auth-forgot-back"
        >
          <ArrowLeft className="h-4 w-4" />{t('Zurück zur Anmeldung', 'Back to sign in')}
        </button>

        <div className="space-y-1">
          <h3 className="font-heading text-lg font-semibold text-foreground">
            {t('Passwort zurücksetzen', 'Reset password')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {forgotStep === 'request'
              ? t('Gib deine E-Mail oder deinen Benutzernamen ein. Wir senden dir einen 6-stelligen Code.',
                  'Enter your email or username. We will send you a 6-digit code.')
              : t('Gib den 6-stelligen Code aus der E-Mail und dein neues Passwort ein.',
                  'Enter the 6-digit code from the email and your new password.')}
          </p>
        </div>

        {forgotError ? <ErrorNote>{forgotError}</ErrorNote> : null}

        {forgotStep === 'request' ? (
          <form onSubmit={submitForgotRequest} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="forgot-id">{t('E-Mail oder Benutzername', 'Email or username')}</Label>
              <div className="relative">
                <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="forgot-id" type="text" required value={forgotId} onChange={(e) => setForgotId(e.target.value)}
                  placeholder={t('name@example.com oder benutzername', 'name@example.com or username')} className="pl-9"
                  data-testid="auth-forgot-id-input" autoComplete="username" />
              </div>
            </div>
            <Button type="submit" className="w-full gap-2" disabled={busy} data-testid="auth-forgot-request-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              {t('Code senden', 'Send code')}
            </Button>
            <button type="button" onClick={() => setForgotStep('reset')}
              className="w-full text-center text-xs text-muted-foreground underline-offset-2 hover:underline"
              data-testid="auth-forgot-have-code">
              {t('Ich habe bereits einen Code', 'I already have a code')}
            </button>
          </form>
        ) : (
          <form onSubmit={submitForgotReset} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="reset-code">{t('6-stelliger Code', '6-digit code')}</Label>
              <div className="relative">
                <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="reset-code" type="text" inputMode="numeric" maxLength={6} required value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456" className="pl-9 tracking-[0.4em]" data-testid="auth-reset-code-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reset-pw">{t('Neues Passwort', 'New password')}</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="reset-pw" type="password" required minLength={8} value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('Mindestens 8 Zeichen', 'At least 8 characters')} className="pl-9"
                  data-testid="auth-reset-password-input" autoComplete="new-password" />
              </div>
            </div>
            <Button type="submit" className="w-full gap-2" disabled={busy} data-testid="auth-reset-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              {t('Passwort ändern', 'Change password')}
            </Button>
          </form>
        )}
      </div>
    );
  }

  // ----- Login / Register view -----
  return (
    <div className="space-y-5">
      <Button
        type="button"
        variant="outline"
        className="relative w-full gap-2.5 border-border/80 py-5 text-[15px] font-semibold"
        onClick={startGoogle}
        data-testid="auth-google-btn"
      >
        <GoogleIcon />
        {t('Mit Google fortfahren', 'Continue with Google')}
        {lastMethod === 'google' ? (
          <span className="absolute right-2 top-2"><LastUsedBadge /></span>
        ) : null}
      </Button>

      <label className="flex cursor-pointer items-center justify-center gap-2 text-xs text-muted-foreground" data-testid="auth-google-remember">
        <Checkbox checked={rememberGoogle} onCheckedChange={(v) => setRememberGoogle(!!v)} data-testid="auth-google-remember-checkbox" />
        {t('Mit Google angemeldet bleiben', 'Stay signed in with Google')}
      </label>

      <div className="flex items-center gap-3" aria-hidden="true">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('oder', 'or')}</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setLoginError(''); setRegisterError(''); }} className="w-full">
        <TabsList className="mb-4 grid w-full grid-cols-2">
          <TabsTrigger value="login" data-testid="auth-login-tab" className="gap-1.5">
            <LogIn className="h-4 w-4" />{t('Anmelden', 'Sign in')}
          </TabsTrigger>
          <TabsTrigger value="register" data-testid="auth-register-tab" className="gap-1.5">
            <UserPlus className="h-4 w-4" />{t('Registrieren', 'Register')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="login" className="mt-0">
          <form onSubmit={submitLogin} className="space-y-4">
            {lastMethod === 'password' ? (
              <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground" data-testid="auth-login-last-used">
                <History className="h-3.5 w-3.5 text-primary" />
                {t('Zuletzt mit E-Mail/Passwort angemeldet.', 'Last signed in with email/password.')}
              </div>
            ) : null}
            {loginError ? <ErrorNote>{loginError}</ErrorNote> : null}
            <div className="space-y-1.5">
              <Label htmlFor="login-id">{t('E-Mail oder Benutzername', 'Email or username')}</Label>
              <div className="relative">
                <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="login-id" type="text" required value={identifier} onChange={(e) => { setIdentifier(e.target.value); if (loginError) setLoginError(''); }}
                  placeholder={t('name@example.com oder benutzername', 'name@example.com or username')} className="pl-9" data-testid="auth-email-input" autoComplete="username" />
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="login-password">{t('Passwort', 'Password')}</Label>
                <button type="button" onClick={openForgot}
                  className="text-xs font-medium text-primary underline-offset-2 hover:underline"
                  data-testid="auth-forgot-link">
                  {t('Passwort vergessen?', 'Forgot password?')}
                </button>
              </div>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="login-password" type="password" required value={password} onChange={(e) => { setPassword(e.target.value); if (loginError) setLoginError(''); }}
                  placeholder="••••••••" className="pl-9" data-testid="auth-password-input" autoComplete="current-password" />
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground" data-testid="auth-login-remember">
              <Checkbox checked={rememberLogin} onCheckedChange={(v) => setRememberLogin(!!v)} data-testid="auth-login-remember-checkbox" />
              {t('Angemeldet bleiben', 'Stay signed in')}
            </label>
            {siteKey ? (
              <div className="space-y-2 rounded-xl border border-primary/30 bg-primary/5 p-3" data-testid="auth-captcha">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold text-foreground">
                    {t('Sicherheitsprüfung', 'Security check')}
                  </span>
                </div>
                <Turnstile siteKey={siteKey} action="login" onToken={(tok) => { setLoginToken(tok); if (tok && loginError) setLoginError(''); }} />
                <p className="text-[11px] text-muted-foreground">
                  {t('Bitte bestätige, dass du kein Roboter bist, um fortzufahren.',
                     'Please confirm you are not a robot to continue.')}
                </p>
              </div>
            ) : null}
            <Button type="submit" className="w-full gap-2" disabled={busy} data-testid="auth-login-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
              {t('Anmelden', 'Sign in')}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="register" className="mt-0">
          <form onSubmit={submitRegister} className="space-y-4">
            {registerError ? <ErrorNote>{registerError}</ErrorNote> : null}
            <div className="space-y-1.5">
              <Label htmlFor="reg-username">{t('Benutzername', 'Username')}</Label>
              <div className="relative">
                <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="reg-username" type="text" required minLength={3} maxLength={30} value={username}
                  onChange={(e) => { setUsername(e.target.value); if (registerError) setRegisterError(''); }}
                  placeholder={t('z. B. max_mustermann', 'e.g. max_mustermann')} className="pl-9"
                  data-testid="auth-username-input" autoComplete="username" />
              </div>
              <p className="text-[11px] text-muted-foreground">
                {t('3–30 Zeichen · Buchstaben, Zahlen, . _ -', '3–30 chars · letters, digits, . _ -')}
              </p>
              <UsernameAvailability value={username} onPick={(s) => { setUsername(s); if (registerError) setRegisterError(''); }} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-name">{t('Name (optional)', 'Name (optional)')}</Label>
              <div className="relative">
                <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="reg-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder={t('Dein Name', 'Your name')} className="pl-9" data-testid="auth-name-input" autoComplete="name" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-email">{t('E-Mail', 'Email')}</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="reg-email" type="email" required value={email} onChange={(e) => { setEmail(e.target.value); if (registerError) setRegisterError(''); }}
                  placeholder="name@example.com" className="pl-9" data-testid="auth-register-email-input" autoComplete="email" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-password">{t('Passwort', 'Password')}</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="reg-password" type="password" required minLength={8} value={password} onChange={(e) => { setPassword(e.target.value); if (registerError) setRegisterError(''); }}
                  placeholder={t('Mindestens 8 Zeichen', 'At least 8 characters')} className="pl-9" data-testid="auth-register-password-input" autoComplete="new-password" />
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground" data-testid="auth-register-remember">
              <Checkbox checked={rememberRegister} onCheckedChange={(v) => setRememberRegister(!!v)} data-testid="auth-register-remember-checkbox" />
              {t('Angemeldet bleiben', 'Stay signed in')}
            </label>
            {siteKey ? (
              <div className="space-y-2 rounded-xl border border-primary/30 bg-primary/5 p-3" data-testid="auth-register-captcha">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold text-foreground">
                    {t('Sicherheitsprüfung', 'Security check')}
                  </span>
                </div>
                <Turnstile siteKey={siteKey} action="register" onToken={(tok) => { setRegisterToken(tok); if (tok && registerError) setRegisterError(''); }} />
                <p className="text-[11px] text-muted-foreground">
                  {t('Bitte bestätige, dass du kein Roboter bist.', 'Please confirm you are not a robot.')}
                </p>
              </div>
            ) : null}
            <Button type="submit" className="w-full gap-2" disabled={busy} data-testid="auth-register-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              {t('Konto erstellen', 'Create account')}
            </Button>
          </form>
        </TabsContent>
      </Tabs>

      <p className="text-center text-xs text-muted-foreground">
        {t('Mit der Anmeldung stimmst du unseren ', 'By signing in you agree to our ')}
        <a href={en ? '/en/agb/' : '/agb/'} className="text-primary underline-offset-2 hover:underline">{t('AGB', 'Terms')}</a>
        {t(' und der ', ' and ')}
        <a href={en ? '/en/datenschutz/' : '/datenschutz/'} className="text-primary underline-offset-2 hover:underline">{t('Datenschutzerklärung', 'Privacy Policy')}</a>
        {t(' zu.', '.')}
      </p>
    </div>
  );
};

/**
 * Global login dialog. Opens whenever a guest triggers a login-gated action.
 * Controlled through the auth context (authPromptOpen / closeAuthPrompt).
 */
export const AuthDialog = () => {
  const { authPromptOpen, closeAuthPrompt, authPromptReason, user } = useAuth();
  const { lang } = useLang();
  const en = lang === 'en';

  // Auto-close once the user is authenticated.
  useEffect(() => {
    if (user && authPromptOpen) closeAuthPrompt();
  }, [user, authPromptOpen, closeAuthPrompt]);

  return (
    <Dialog open={authPromptOpen} onOpenChange={(o) => { if (!o) closeAuthPrompt(); }}>
      <DialogContent className="sm:max-w-md" data-testid="auth-dialog">
        <DialogHeader className="items-center text-center">
          <div className="mx-auto mb-1 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
            <ScanBarcode className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="font-heading text-xl">
            {en ? 'Sign in to continue' : 'Zum Fortfahren anmelden'}
          </DialogTitle>
          <DialogDescription>
            {authPromptReason
              || (en
                ? 'Create a free account to generate, download and export your codes.'
                : 'Erstelle ein kostenloses Konto, um Codes zu erzeugen, herunterzuladen und zu exportieren.')}
          </DialogDescription>
        </DialogHeader>
        <AuthForms onDone={closeAuthPrompt} />
      </DialogContent>
    </Dialog>
  );
};

/**
 * One-time username prompt. Shown to signed-in accounts that don't yet have a
 * username (e.g. Google sign-ins, or legacy accounts created before usernames
 * existed). Lets them pick a unique username so they can also log in with it.
 */
export const UsernamePrompt = () => {
  const { user, setUsername: setUsernameApi } = useAuth();
  const { lang } = useLang();
  const en = lang === 'en';
  const t = (de, eng) => (en ? eng : de);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const skippedThisSession = () => {
    try { return sessionStorage.getItem('bcs-username-prompt-skip') === '1'; } catch (e) { return false; }
  };

  useEffect(() => {
    // Show once the user is loaded and has no username yet (and hasn't skipped).
    if (user && !user.username && !skippedThisSession()) {
      setOpen(true);
    } else if (!user || user.username) {
      setOpen(false);
    }
  }, [user]);

  const skip = () => {
    try { sessionStorage.setItem('bcs-username-prompt-skip', '1'); } catch (e) { /* ignore */ }
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      await setUsernameApi(value.trim());
      toast.success(t('Benutzername gespeichert.', 'Username saved.'));
      setOpen(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : t('Konnte nicht gespeichert werden.', 'Could not save.'));
    } finally { setBusy(false); }
  };

  if (!user || user.username) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) skip(); }}>
      <DialogContent className="sm:max-w-md" data-testid="username-prompt-dialog">
        <DialogHeader className="items-center text-center">
          <div className="mx-auto mb-1 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
            <AtSign className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="font-heading text-xl">
            {t('Benutzernamen wählen', 'Choose a username')}
          </DialogTitle>
          <DialogDescription>
            {t('Lege einen Benutzernamen fest, damit du dich künftig auch ohne E-Mail anmelden kannst.',
               'Set a username so you can also sign in without your email in future.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {error ? (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert" data-testid="username-prompt-error">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{error}</span>
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="up-username">{t('Benutzername', 'Username')}</Label>
            <div className="relative">
              <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="up-username" type="text" required minLength={3} maxLength={30} value={value}
                onChange={(e) => { setValue(e.target.value); if (error) setError(''); }}
                placeholder={t('z. B. max_mustermann', 'e.g. max_mustermann')} className="pl-9"
                data-testid="username-prompt-input" autoComplete="username" autoFocus />
            </div>
            <p className="text-[11px] text-muted-foreground">
              {t('3–30 Zeichen · Buchstaben, Zahlen, . _ -', '3–30 chars · letters, digits, . _ -')}
            </p>
            <UsernameAvailability value={value} onPick={(s) => { setValue(s); if (error) setError(''); }} />
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" className="flex-1" onClick={skip} data-testid="username-prompt-skip">
              {t('Später', 'Later')}
            </Button>
            <Button type="submit" className="flex-1 gap-2" disabled={busy} data-testid="username-prompt-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <AtSign className="h-4 w-4" />}
              {t('Speichern', 'Save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Wrapper that gates whole tools (e.g. Batch, Labels) behind login while
 * keeping the page publicly viewable. Shows a prompt card for guests.
 */
export const AuthRequired = ({ children, title, description }) => {
  const { user, promptLogin } = useAuth();
  const { lang } = useLang();
  const en = lang === 'en';
  if (user) return children;
  return (
    <Card className="border-dashed" data-testid="auth-required">
      <CardContent className="flex flex-col items-center gap-3 px-6 py-12 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
          <Lock className="h-7 w-7 text-primary" />
        </div>
        <h3 className="font-heading text-lg font-semibold text-foreground">
          {title || (en ? 'Sign in to use this tool' : 'Zum Nutzen bitte anmelden')}
        </h3>
        <p className="max-w-sm text-sm text-muted-foreground">
          {description || (en
            ? 'Viewing is free — creating and exporting requires a free account.'
            : 'Ansehen ist kostenlos – zum Erstellen und Exportieren ist ein kostenloses Konto nötig.')}
        </p>
        <Button className="mt-1 gap-2" onClick={() => promptLogin()} data-testid="auth-required-login-btn">
          <LogIn className="h-4 w-4" />{en ? 'Sign in' : 'Anmelden'}
        </Button>
      </CardContent>
    </Card>
  );
};

// One-time exchange of the OAuth session_id (URL fragment) for an app session.
export const AuthCallback = () => {
  const { loginWithSession } = useAuth();
  const { lang } = useLang();
  const processed = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    let sid = '';
    try {
      const h = window.location.hash || '';
      const m = h.match(/session_id=([^&]+)/);
      if (m) sid = decodeURIComponent(m[1]);
    } catch (e) { /* noop */ }
    if (!sid) { setFailed(true); return; }
    let remember = true;
    try { remember = localStorage.getItem('bc_google_remember') !== '0'; } catch (e) { /* ignore */ }
    (async () => {
      try {
        await loginWithSession(sid, remember);
        try { localStorage.removeItem('bc_google_remember'); } catch (e) { /* ignore */ }
        window.location.replace(window.location.origin + '/');
      } catch (e) {
        setFailed(true);
      }
    })();
  }, [loginWithSession]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-3 text-center">
        {failed ? (
          <>
            <p className="font-heading text-foreground">
              {lang === 'en' ? 'Sign-in could not be completed.' : 'Die Anmeldung konnte nicht abgeschlossen werden.'}
            </p>
            <a href="/" className="text-sm text-primary underline-offset-2 hover:underline" data-testid="auth-callback-retry">
              {lang === 'en' ? 'Back to app' : 'Zurück zur App'}
            </a>
          </>
        ) : (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              {lang === 'en' ? 'Signing you in…' : 'Anmeldung wird abgeschlossen…'}
            </p>
          </>
        )}
      </div>
    </div>
  );
};
